import { saveAs } from 'file-saver'
import html2canvas from 'html2canvas'

import message from '@/components/Message'
import { SelectOption } from '@/components/Select'

interface UseExportOptions {
  contentRef: React.RefObject<HTMLDivElement>
}

export const useVisualDetail = ({ contentRef }: UseExportOptions) => {
  const [exportLoading, setExportLoading] = useState(false)

  // const getViewportSize = (element: HTMLElement) => {
  //   const { width, height } = element.getBoundingClientRect()
  //   const targetWidth = 1200 // 设置目标宽度
  //   const scale = targetWidth / width
  //   const targetHeight = Math.floor(height * scale)

  //   return {
  //     width: targetWidth,
  //     height: targetHeight,
  //   }
  // }

  const copyToClipboard = async () => {
    if (!contentRef.current?.shadowRoot) return

    const content = contentRef.current.shadowRoot.children[1] as HTMLElement

    const canvas = await html2canvas(content, {
      scale: 3,
      useCORS: true,
      backgroundColor: '#ffffff',
    })

    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          message.info('Image copied to clipboard!')
        } catch (err) {
          console.error('Failed to copy image:', err)
          message.error('Failed to copy image! Please try again.')
        }
      }
    })
  }

  const exportFile = async (format: string) => {
    if (!contentRef.current?.shadowRoot) return
    const content = contentRef.current.shadowRoot.children[1]

    switch (format) {
      case 'png':
      case 'jpg': {
        const canvas = await html2canvas(content as HTMLElement, {
          scale: 3,
          useCORS: true,
          backgroundColor: '#ffffff',
        })
        canvas.toBlob((blob) => {
          if (blob) {
            message.info('Image exported!')
            saveAs(blob, `creavo-export.${format}`)
          }
        }, `image/${format}`)
        break
      }
      case 'svg': {
        if (!content) return

        // 克隆节点以避免修改原始内容
        const container = content.cloneNode(true) as HTMLElement

        // 收集所有样式
        const styles = document.styleSheets
        let cssText = ''
        for (const sheet of Array.from(styles)) {
          try {
            const rules = Array.from(sheet.cssRules)
            cssText += rules.map((rule) => rule.cssText).join('\n')
          } catch (e) {
            console.warn('无法读取样式表:', e)
          }
        }

        // 创建 SVG
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
        svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
        svg.setAttribute('width', '100%')
        svg.setAttribute('height', '100%')

        // 添加样式
        const styleElement = document.createElement('style')
        styleElement.textContent = cssText
        svg.appendChild(styleElement)

        // 添加外部字体
        const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
        const fontFace = document.createElement('style')
        fontFace.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');
  `
        defs.appendChild(fontFace)
        svg.appendChild(defs)

        // 添加内容
        const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject')
        foreignObject.setAttribute('width', '100%')
        foreignObject.setAttribute('height', '100%')
        foreignObject.appendChild(container)
        svg.appendChild(foreignObject)

        // 导出
        const svgContent = new XMLSerializer().serializeToString(svg)
        const blob = new Blob([svgContent], { type: 'image/svg+xml' })
        saveAs(blob, 'creavo-export.svg')
        message.info('SVG exported!')
        break
      }
      case 'html': {
        const htmlContent = content.innerHTML
        const blob = new Blob([htmlContent], { type: 'text/html' })
        saveAs(blob, 'creavo-export.html')
        message.info('HTML exported!')
        break
      }
    }
  }

  const handleSelectChange = async (option: SelectOption) => {
    setExportLoading(true)
    switch (option.value) {
      case 'copyImg':
        await copyToClipboard()
        break
      case 'copyLink':
        await navigator.clipboard.writeText(window.location.href)
        break
      case 'png':
      case 'jpg':
      case 'svg':
      case 'html':
        await exportFile(option.value)
        break
    }
    setExportLoading(false)
  }

  return {
    exportLoading,
    handleSelectChange,
  }
}
